import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, Eye, Star, ShieldCheck, MapPin, Briefcase, Image } from '@phosphor-icons/react';

const CATS = ['', 'plumbing', 'electrical', 'ac_cooling', 'carpentry', 'painting', 'tiling', 'welding', 'cleaning'];

export default function BrowsePortfoliosPage() {
  const { artisanId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [artisan, setArtisan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        if (artisanId) {
          const { data } = await api.get(`/portfolio/artisan/${artisanId}`);
          setItems(data.items || []);
          setArtisan(data.artisan);
        } else {
          const { data } = await api.get('/portfolio/browse', { params: filter ? { category: filter } : {} });
          setItems(data.items || []);
        }
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [artisanId, filter, api]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="browse-portfolios-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#C9BAA8]/40">
        <div className="px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#DFD3C5] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
          <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">{artisan ? `${artisan.name}'s Work` : 'Artisan Portfolios'}</h1>
        </div>
        {!artisanId && (
          <div className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide">
            {CATS.map(c => (
              <button key={c} onClick={() => { setFilter(c); setLoading(true); }} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === c ? 'bg-[#E86A33]/10 text-[#E86A33] border border-[#E86A33]' : 'bg-white text-[#8C8178] border border-[#D4C5B3]'}`} data-testid={`portfolio-filter-${c || 'all'}`}>
                {c ? c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'All'}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Artisan Profile Card */}
      {artisan && (
        <div className="mx-4 mt-4 bg-gradient-to-br from-[#1A4D2E] to-[#0F3A20] rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute top-[-30px] right-[-30px] w-[120px] h-[120px] bg-white/5 rounded-full" />
          <div className="flex items-center gap-3 relative z-10">
            <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center font-heading text-xl font-bold">{artisan.name?.charAt(0)?.toUpperCase()}</div>
            <div>
              <h3 className="font-heading text-lg font-bold">{artisan.name}</h3>
              <div className="flex items-center gap-2 mt-1 text-xs text-white/70">
                <span>{artisan.trade?.replace(/_/g, ' ')}</span>
                {artisan.lga && <><span>·</span><span className="flex items-center gap-1"><MapPin size={10} />{artisan.lga}</span></>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-4 relative z-10">
            <div className="bg-white/10 rounded-lg p-2 text-center"><Star size={14} className="mx-auto text-[#F4A261]" /><span className="text-sm font-bold">{artisan.star_rating?.toFixed(1) || '0.0'}</span></div>
            <div className="bg-white/10 rounded-lg p-2 text-center"><ShieldCheck size={14} className="mx-auto text-[#F4A261]" /><span className="text-sm font-bold">{artisan.trust_score || 0}</span></div>
            <div className="bg-white/10 rounded-lg p-2 text-center"><Briefcase size={14} className="mx-auto" /><span className="text-sm font-bold">{artisan.jobs_completed || 0}</span></div>
          </div>
        </div>
      )}

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <Image size={48} className="text-[#D4C9BC] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">No portfolios yet</h3>
            <p className="text-sm text-[#8C8178] font-body">Artisan portfolios will appear here as they upload work.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className="bg-white rounded-2xl border border-[#D4C5B3] overflow-hidden shadow-sm" data-testid={`browse-item-${item.id}`}>
                {item.after_photo_data && (
                  <div className="relative aspect-video bg-[#E8DDD2]">
                    <img src={item.after_photo_data} alt={item.title} className="w-full h-full object-cover" />
                    <span className="absolute top-2 right-2 bg-[#2D6A4F]/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">COMPLETED</span>
                  </div>
                )}
                <div className="p-4">
                  <h4 className="font-heading text-base font-bold text-[#1C1C1C]">{item.title}</h4>
                  {item.description && <p className="text-xs text-[#8C8178] mt-1 font-body">{item.description}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#DDD0C1]">
                    <div className="flex items-center gap-2">
                      {!artisanId && (
                        <Link to={`/portfolios/${item.user_id}`} className="text-xs font-bold text-[#1A4D2E] hover:underline" data-testid={`view-artisan-${item.user_id}`}>{item.artisan_name}</Link>
                      )}
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E86A33]/10 text-[#E86A33]">{item.category?.replace(/_/g, ' ')}</span>
                    </div>
                    <span className="text-xs text-[#8C8178] flex items-center gap-1"><Eye size={12} /> {item.views || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
